import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';
import type {
  CreateBookingPayload,
  CreateBookingResponse,
} from '@/features/bookings/types/booking.types';

/** Guest-facing fields from a create-booking `code: 203` body. */
export interface ExistingBookingNotice {
  message: string;
  bookingDate: string;
  bookingTime: string;
  numGuests: number | null;
  numChildren: number | null;
  eventName: string;
  menuNames: string;
  noMenuName: string;
  services: string;
  status: string;
  branchName: string;
}

export type PublicCreateBookingResult =
  | { outcome: 'created'; booking: CreateBookingResponse }
  | { outcome: 'existing'; notice: ExistingBookingNotice };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'object' && value !== null) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function formatSlotTime(hour: number, minutes: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function readBookingTime(results: Record<string, unknown>): string {
  const direct = asText(results.bookingTime);
  if (direct) {
    return direct;
  }

  const slot = asRecord(results.timeSlot);
  const hour = asFiniteNumber(results.bookingHour) ?? asFiniteNumber(slot?.bookingHour);
  const minutes =
    asFiniteNumber(results.bookingMinutes) ??
    asFiniteNumber(slot?.bookingMinutes) ??
    0;

  return hour == null ? '' : formatSlotTime(hour, minutes);
}

function readMenuLabels(results: Record<string, unknown>): {
  menuNames: string;
  noMenuName: string;
} {
  if (!Array.isArray(results.formMenus)) {
    return { menuNames: '', noMenuName: '' };
  }

  const menuNames: string[] = [];
  const noMenuNames: string[] = [];

  for (const item of results.formMenus) {
    const row = asRecord(item);
    if (!row) {
      continue;
    }

    const name = asText(row.name);
    const code = asText(row.formMenuCode);
    const label = name || code;
    if (!label) {
      continue;
    }

    const isNoMenu = row.isNoMenu === true || code === 'NO_MENU';
    if (isNoMenu) {
      noMenuNames.push(label);
    } else {
      menuNames.push(label);
    }
  }

  return {
    menuNames: menuNames.join(', '),
    noMenuName: noMenuNames.join(', '),
  };
}

function readExistingBookingNotice(
  response: unknown,
): ExistingBookingNotice | null {
  const record = asRecord(response);
  if (!record || record.code !== 203) {
    return null;
  }

  const results = asRecord(record.results) ?? {};
  const menus = readMenuLabels(results);
  const children = asFiniteNumber(results.numChildren);
  const event = asRecord(results.event);

  return {
    message: asText(record.message),
    bookingDate: asText(results.bookingDate),
    bookingTime: readBookingTime(results),
    numGuests: asFiniteNumber(results.numGuests),
    numChildren: children != null && children > 0 ? children : null,
    eventName: asText(event?.eventName),
    menuNames: menus.menuNames,
    noMenuName: menus.noMenuName,
    services: asText(results.extraServiceChoiceValue),
    status: asText(results.status),
    branchName: asText(results.branchName),
  };
}

export const publicBookingCreateApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createPublicBooking: builder.mutation<
      PublicCreateBookingResult,
      CreateBookingPayload
    >({
      query: (body) => ({
        url: '/booking/create',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown): PublicCreateBookingResult => {
        const notice = readExistingBookingNotice(response);
        if (notice) {
          return { outcome: 'existing', notice };
        }
        return {
          outcome: 'created',
          booking: normalizePro20Response(
            response as
              | Pro20ApiResponse<CreateBookingResponse>
              | CreateBookingResponse,
          ),
        };
      },
      invalidatesTags: (result, _error, { branchCode }) => {
        if (result?.outcome === 'existing') {
          return [];
        }
        return [
          { type: API_TAGS.Bookings, id: branchCode },
          { type: API_TAGS.Bookings, id: `${branchCode}-calendar` },
        ];
      },
    }),
  }),
});

export const { useCreatePublicBookingMutation } = publicBookingCreateApi;
